<template>
  <div id="manage-vehicles" class="page-content">
    <div class="page-header">
      <h1 class="title">Vehicle &amp; Family Manager</h1>
      <div class="stats-bar" v-if="stats">
        <div class="stat-item">
          <span class="stat-value">{{ stats.totalVehicles }}</span>
          <span class="stat-label">Vehicles</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ stats.activeVehicles }}</span>
          <span class="stat-label">Active</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ stats.totalFamilies }}</span>
          <span class="stat-label">Families</span>
        </div>
        <div class="stat-item warn">
          <span class="stat-value">{{ stats.unassignedVehicles }}</span>
          <span class="stat-label">Unassigned</span>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="navigation">
      <div class="bar">
        <button
          @click="activeTab = 'vehicles'"
          class="tablinks"
          :class="{ active: activeTab === 'vehicles' }"
        >
          🚗 Vehicles
        </button>
        <button
          @click="activeTab = 'families'"
          class="tablinks"
          :class="{ active: activeTab === 'families' }"
        >
          👥 Families
        </button>
        <button
          @click="activeTab = 'bulk_assign'"
          class="tablinks"
          :class="{ active: activeTab === 'bulk_assign' }"
        >
          ⚡ Bulk Assign products to families
        </button>
      </div>
    </div>

    <!-- ═══════════ VEHICLES TAB ═══════════ -->
    <div v-if="activeTab === 'vehicles'" class="tab-content">
      <div class="toolbar">
        <button @click="openVehicleModal(null)" class="btn-dark-button">
          + New Vehicle
        </button>
        <button @click="showVehicleUpload = true" class="btn-clear-button">
          ⬆ Bulk Upload
        </button>
        <button
          @click="exportVehiclesWithFamilies"
          :disabled="exportingVehicles"
          class="btn-clear-button"
        >
          {{ exportingVehicles ? "Exporting..." : "⬇ Export Vehicles" }}
        </button>
        <div class="toolbar-right">
          <select
            v-model="vehicleFilters.active"
            @change="loadVehicles()"
            class="regular bordered"
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          <div class="per-page">
            <label>Per page:</label>
            <select
              v-model="vehiclesPerPage"
              @change="
                vehiclePage = 0;
                loadVehicles();
              "
              class="regular bordered"
            >
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Vehicle Filters -->
      <div class="filter-bar">
        <input
          v-model="vehicleFilters.make"
          @input="debouncedLoadVehicles"
          type="text"
          placeholder="Make..."
          class="regular bordered"
        />
        <input
          v-model="vehicleFilters.model"
          @input="debouncedLoadVehicles"
          type="text"
          placeholder="Model..."
          class="regular bordered"
        />
        <input
          v-model="vehicleFilters.year"
          @input="debouncedLoadVehicles"
          type="text"
          placeholder="Year..."
          class="regular bordered"
        />
        <input
          v-model="vehicleFilters.engine_name"
          @input="debouncedLoadVehicles"
          type="text"
          placeholder="Engine..."
          class="regular bordered"
        />
        <input
          v-model="vehicleFilters.region"
          @input="debouncedLoadVehicles"
          type="text"
          placeholder="Region..."
          class="regular bordered"
        />
        <input
          v-model="vehicleFilters.catalog_code"
          @input="debouncedLoadVehicles"
          type="text"
          placeholder="Code..."
          class="regular bordered"
        />
        <button @click="clearVehicleFilters" class="btn-clear-button">
          Clear
        </button>
      </div>

      <!-- Vehicles Table -->
      <div class="table-wrapper" v-if="!loadingVehicles">
        <table class="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Year</th>
              <th>Make</th>
              <th>Model</th>
              <th>Engine</th>
              <th>Liters</th>
              <th>Aspiration</th>
              <th>Region</th>
              <th>Family Code</th>
              <th>Family</th>
              <th>Active</th>
              <th width="120">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in vehicles" :key="v.id">
              <td class="id-cell">{{ v.id }}</td>
              <td>{{ v.year }}</td>
              <td>{{ v.make }}</td>
              <td>{{ v.model }}</td>
              <td>{{ v.engineName }}</td>
              <td>{{ v.engineLiter }}</td>
              <td>{{ v.aspiration }}</td>
              <td>{{ v.region || "—" }}</td>
              <td>
                <code v-if="v.catalogCode" class="code-badge">{{
                  v.catalogCode
                }}</code>
                <span v-else class="text-muted">—</span>
              </td>
              <td>{{ v.vehicleFamily || "—" }}</td>
              <td>
                <button
                  @click.stop="toggleVehicleActive(v)"
                  :class="[
                    'status-toggle',
                    v.active == 1 ? 'is-active' : 'is-inactive'
                  ]"
                  :title="
                    v.active == 1 ? 'Click to deactivate' : 'Click to activate'
                  "
                >
                  {{ v.active == 1 ? "Active" : "Inactive" }}
                </button>
              </td>
              <td class="actions-cell">
                <button
                  @click="openVehicleModal(v)"
                  class="action-btn"
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  @click="openAssignModal(v)"
                  class="action-btn"
                  title="Assign Family"
                >
                  🔗
                </button>
                <button
                  @click="removeVehicle(v)"
                  class="action-btn delete"
                  title="Delete"
                >
                  🗑️
                </button>
              </td>
            </tr>
            <tr v-if="vehicles.length === 0">
              <td colspan="12" class="empty-row">No vehicles found</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="loading-state">Loading vehicles...</div>

      <!-- Vehicle Pagination -->
      <div class="pagination" v-if="vehicleTotal > 0">
        <span>
          Showing {{ vehiclePage * vehiclesPerPage + 1 }}–{{
            Math.min((vehiclePage + 1) * vehiclesPerPage, vehicleTotal)
          }}
          of {{ vehicleTotal }}
        </span>
        <div class="pagination-btns">
          <button
            @click="
              vehiclePage--;
              loadVehicles();
            "
            :disabled="vehiclePage === 0"
            class="btn-dark-button"
          >
            Prev
          </button>
          <span>Page {{ vehiclePage + 1 }} / {{ vehicleTotalPages }}</span>
          <button
            @click="
              vehiclePage++;
              loadVehicles();
            "
            :disabled="vehiclePage >= vehicleTotalPages - 1"
            class="btn-dark-button"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════ FAMILIES TAB ═══════════ -->
    <div v-if="activeTab === 'families'" class="tab-content">
      <div class="toolbar">
        <button @click="openFamilyModal(null)" class="btn-dark-button">
          + New Family
        </button>
        <button @click="showFamilyUpload = true" class="btn-clear-button">
          ⬆ Bulk Upload
        </button>
        <div class="toolbar-right">
          <select
            v-model="familyFilters.active"
            @change="loadFamilies()"
            class="regular bordered"
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>

      <!-- Family Filters -->
      <div class="filter-bar">
        <input
          v-model="familyFilters.catalog_code"
          @input="debouncedLoadFamilies"
          type="text"
          placeholder="Code..."
          class="regular bordered"
        />
        <input
          v-model="familyFilters.serie"
          @input="debouncedLoadFamilies"
          type="text"
          placeholder="Serie..."
          class="regular bordered"
        />
        <input
          v-model="familyFilters.family"
          @input="debouncedLoadFamilies"
          type="text"
          placeholder="Family name..."
          class="regular bordered"
        />
        <button @click="clearFamilyFilters" class="btn-clear-button">
          Clear
        </button>
      </div>

      <!-- Families Grid -->
      <div v-if="!loadingFamilies" class="families-grid">
        <div
          v-for="fam in families"
          :key="fam.catalogCode"
          class="family-card"
          :class="{ inactive: fam.active != 1 }"
        >
          <div class="family-image-container" @click="openImageUpload(fam)">
            <img
              v-if="fam.imageUrl"
              :src="fam.imageUrl"
              :alt="fam.family"
              class="family-image"
            />
            <div v-else class="family-image-placeholder">
              <span>📷</span>
              <small>Add Image</small>
            </div>
            <div class="image-overlay">
              <span>Change Image</span>
            </div>
          </div>
          <div class="family-info">
            <code class="code-badge">{{ fam.catalogCode }}</code>
            <h3 class="family-name">{{ fam.family }}</h3>
            <p class="family-serie">Serie: {{ fam.serie }}</p>
            <div class="family-meta">
              <span class="vehicle-count"
                >{{ fam.vehicleCount || 0 }} vehicles</span
              >
              <span
                :class="['status-dot', fam.active == 1 ? 'active' : 'inactive']"
              ></span>
            </div>
          </div>
          <div class="family-actions">
            <button
              @click="openFamilyModal(fam)"
              class="action-btn"
              title="Edit"
            >
              ✏️
            </button>
            <button
              @click="viewFamilyVehicles(fam)"
              class="action-btn"
              title="View Vehicles"
            >
              🚗
            </button>
            <button
              @click="removeFamily(fam)"
              class="action-btn delete"
              title="Delete"
            >
              🗑️
            </button>
          </div>
        </div>
        <div v-if="families.length === 0" class="empty-state">
          <p>No families found</p>
        </div>
      </div>
      <div v-else class="loading-state">Loading families...</div>
    </div>

    <!-- ═══════════ VEHICLE EDIT MODAL ═══════════ -->
    <div
      v-if="showVehicleModal"
      class="modal"
      @click.self="showVehicleModal = false"
    >
      <div class="modal-content modal-medium">
        <div class="modal-header">
          <h2>{{ editingVehicle.id ? "Edit Vehicle" : "New Vehicle" }}</h2>
          <button @click="showVehicleModal = false" class="modal-close">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div class="form-grid">
            <div class="form-group">
              <label>Year *</label>
              <input
                v-model="editingVehicle.year"
                type="number"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Make *</label>
              <input
                v-model="editingVehicle.make"
                type="text"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Model *</label>
              <input
                v-model="editingVehicle.model"
                type="text"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Engine Name</label>
              <input
                v-model="editingVehicle.engine_name"
                type="text"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Engine Liters</label>
              <input
                v-model="editingVehicle.engine_liter"
                type="number"
                step="0.1"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Aspiration</label>
              <input
                v-model="editingVehicle.aspiration"
                type="text"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Cylinders</label>
              <input
                v-model="editingVehicle.cylinders"
                type="number"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Block Type</label>
              <input
                v-model="editingVehicle.block_type"
                type="text"
                maxlength="1"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Region</label>
              <input
                v-model="editingVehicle.region"
                type="text"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Family Code</label>
              <select
                v-model="editingVehicle.catalog_code"
                class="regular full bordered"
              >
                <option :value="null">— No family —</option>
                <option
                  v-for="f in allFamilies"
                  :key="f.catalogCode"
                  :value="f.catalogCode"
                >
                  [{{ f.catalogCode }}] {{ f.family }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>Active</label>
              <select
                v-model="editingVehicle.active"
                class="regular full bordered"
              >
                <option :value="1">Active</option>
                <option :value="0">Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showVehicleModal = false" class="btn-clear-button">
            Cancel
          </button>
          <button
            @click="saveVehicle"
            :disabled="savingVehicle"
            class="btn-dark-button"
          >
            {{ savingVehicle ? "Saving..." : "Save Vehicle" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════ FAMILY EDIT MODAL ═══════════ -->
    <div
      v-if="showFamilyModal"
      class="modal"
      @click.self="showFamilyModal = false"
    >
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h2>{{ editingFamily._isNew ? "New Family" : "Edit Family" }}</h2>
          <button @click="showFamilyModal = false" class="modal-close">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div class="form-stack">
            <div class="form-group">
              <label>Catalog Code *</label>
              <input
                v-model="editingFamily.catalog_code"
                type="text"
                class="regular full bordered"
                :disabled="!editingFamily._isNew"
                maxlength="20"
              />
            </div>
            <div class="form-group">
              <label>Serie *</label>
              <input
                v-model="editingFamily.serie"
                type="text"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Family Name *</label>
              <input
                v-model="editingFamily.family"
                type="text"
                class="regular full bordered"
              />
            </div>
            <div class="form-group">
              <label>Active</label>
              <select
                v-model="editingFamily.active"
                class="regular full bordered"
              >
                <option :value="1">Active</option>
                <option :value="0">Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showFamilyModal = false" class="btn-clear-button">
            Cancel
          </button>
          <button
            @click="saveFamily"
            :disabled="savingFamily"
            class="btn-dark-button"
          >
            {{ savingFamily ? "Saving..." : "Save Family" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════ ASSIGN FAMILY MODAL ═══════════ -->
    <div
      v-if="showAssignModal"
      class="modal"
      @click.self="showAssignModal = false"
    >
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h2>Assign Family</h2>
          <button @click="showAssignModal = false" class="modal-close">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <p class="assign-info">
            Vehicle:
            <strong
              >{{ assignTarget.year }} {{ assignTarget.make }}
              {{ assignTarget.model }}</strong
            >
          </p>
          <div class="form-group">
            <label>Select Family</label>
            <select v-model="assignCatalogCode" class="regular full bordered">
              <option value="">— Remove assignment —</option>
              <option
                v-for="f in allFamilies"
                :key="f.catalogCode"
                :value="f.catalogCode"
              >
                [{{ f.catalogCode }}] {{ f.family }} ({{ f.serie }})
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showAssignModal = false" class="btn-clear-button">
            Cancel
          </button>
          <button
            @click="saveAssignment"
            :disabled="savingAssign"
            class="btn-dark-button"
          >
            {{ savingAssign ? "Saving..." : "Save Assignment" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════ FAMILY VEHICLES MODAL ═══════════ -->
    <div
      v-if="showFamilyVehiclesModal"
      class="modal"
      @click.self="showFamilyVehiclesModal = false"
    >
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h2>
            <code class="code-badge">{{ viewingFamily.catalogCode }}</code>
            {{ viewingFamily.family }} — Vehicles
          </h2>
          <button @click="showFamilyVehiclesModal = false" class="modal-close">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div v-if="loadingFamilyVehicles" class="loading-state">
            Loading...
          </div>
          <div v-else>
            <div class="family-vehicles-toolbar">
              <p class="mb-0">
                {{ familyVehiclesList.length }} vehicle(s) in this family
              </p>
              <button @click="openAddVehiclesModal" class="btn-dark-button">
                + Add Vehicles
              </button>
            </div>
            <div class="table-wrapper" v-if="familyVehiclesList.length > 0">
              <table class="data-table compact">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Year</th>
                    <th>Make</th>
                    <th>Model</th>
                    <th>Engine</th>
                    <th>Region</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="v in familyVehiclesList" :key="v.id">
                    <td class="id-cell">{{ v.id }}</td>
                    <td>{{ v.year }}</td>
                    <td>{{ v.make }}</td>
                    <td>{{ v.model }}</td>
                    <td>{{ v.engineName }} {{ v.engineLiter }}L</td>
                    <td>{{ v.region || "—" }}</td>
                    <td>
                      <button
                        @click="unassignFromFamily(v)"
                        class="action-btn delete"
                        title="Remove from family"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════ ADD VEHICLES TO FAMILY MODAL ═══════════ -->
    <div
      v-if="showAddVehiclesModal"
      class="modal"
      @click.self="showAddVehiclesModal = false"
    >
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h2>
            Add Vehicles to
            <code class="code-badge">{{ viewingFamily.catalogCode }}</code>
            {{ viewingFamily.family }}
          </h2>
          <button @click="showAddVehiclesModal = false" class="modal-close">
            ✕
          </button>
        </div>
        <div class="modal-body">
          <div class="filter-bar">
            <input
              v-model="addVehiclesFilters.make"
              @input="debouncedSearchAddVehicles"
              type="text"
              placeholder="Make..."
              class="regular bordered"
            />
            <input
              v-model="addVehiclesFilters.model"
              @input="debouncedSearchAddVehicles"
              type="text"
              placeholder="Model..."
              class="regular bordered"
            />
            <input
              v-model="addVehiclesFilters.year"
              @input="debouncedSearchAddVehicles"
              type="text"
              placeholder="Year..."
              class="regular bordered"
            />
            <input
              v-model="addVehiclesFilters.engine_name"
              @input="debouncedSearchAddVehicles"
              type="text"
              placeholder="Engine..."
              class="regular bordered"
            />
            <input
              v-model="addVehiclesFilters.region"
              @input="debouncedSearchAddVehicles"
              type="text"
              placeholder="Region..."
              class="regular bordered"
            />
            <label class="inline-check">
              <input
                type="checkbox"
                v-model="addVehiclesUnassignedOnly"
                @change="searchAddVehicles"
              />
              Unassigned only
            </label>
          </div>
          <div v-if="loadingAddVehicles" class="loading-state">
            Loading...
          </div>
          <div v-else>
            <p class="mb-1">
              {{ addVehiclesResults.length }} result(s) —
              {{ addVehiclesSelected.length }} selected
            </p>
            <div class="table-wrapper" v-if="addVehiclesResults.length > 0">
              <table class="data-table compact">
                <thead>
                  <tr>
                    <th width="40">
                      <input
                        type="checkbox"
                        :checked="allAddVehiclesSelected"
                        @change="toggleSelectAllAddVehicles"
                      />
                    </th>
                    <th>ID</th>
                    <th>Year</th>
                    <th>Make</th>
                    <th>Model</th>
                    <th>Engine</th>
                    <th>Region</th>
                    <th>Current Family</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="v in addVehiclesResults"
                    :key="v.id"
                    @click="toggleAddVehicleSelection(v.id)"
                    class="selectable-row"
                  >
                    <td>
                      <input
                        type="checkbox"
                        :checked="addVehiclesSelected.includes(v.id)"
                        @click.stop
                        @change="toggleAddVehicleSelection(v.id)"
                      />
                    </td>
                    <td class="id-cell">{{ v.id }}</td>
                    <td>{{ v.year }}</td>
                    <td>{{ v.make }}</td>
                    <td>{{ v.model }}</td>
                    <td>{{ v.engineName }} {{ v.engineLiter }}L</td>
                    <td>{{ v.region || "—" }}</td>
                    <td>
                      <code v-if="v.catalogCode" class="code-badge">{{
                        v.catalogCode
                      }}</code>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <p>No vehicles found. Adjust filters to search.</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            @click="showAddVehiclesModal = false"
            class="btn-clear-button"
          >
            Cancel
          </button>
          <button
            @click="confirmAddVehicles"
            :disabled="addVehiclesSelected.length === 0 || savingAddVehicles"
            class="btn-dark-button"
          >
            {{
              savingAddVehicles
                ? "Adding..."
                : `Add ${addVehiclesSelected.length} vehicle(s)`
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════ IMAGE UPLOAD MODAL ═══════════ -->
    <div
      v-if="showImageModal"
      class="modal"
      @click.self="showImageModal = false"
    >
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h2>Family Image — {{ imageFamily.family }}</h2>
          <button @click="showImageModal = false" class="modal-close">✕</button>
        </div>
        <div class="modal-body">
          <!-- Mode Toggle -->
          <div class="image-mode-toggle">
            <button
              @click="imageMode = 'upload'"
              :class="['toggle-btn', { active: imageMode === 'upload' }]"
            >
              📷 Upload File
            </button>
            <button
              @click="imageMode = 'url'"
              :class="['toggle-btn', { active: imageMode === 'url' }]"
            >
              🔗 Image URL
            </button>
          </div>

          <!-- File Upload Mode -->
          <div v-if="imageMode === 'upload'">
            <div class="image-preview-box">
              <img
                v-if="imagePreview || imageFamily.imageUrl"
                :src="imagePreview || imageFamily.imageUrl"
                class="preview-img"
              />
              <div v-else class="preview-placeholder">No image</div>
            </div>
            <div class="form-group mt-1">
              <label>Select Image</label>
              <input
                type="file"
                accept="image/*"
                @change="onImageSelected"
                ref="imageInput"
              />
            </div>
          </div>

          <!-- Image URL Mode -->
          <div v-if="imageMode === 'url'">
            <div class="image-preview-box">
              <img
                v-if="imageUrlInput"
                :src="imageUrlInput"
                class="preview-img"
                @error="$event.target.style.display = 'none'"
              />
              <div v-else class="preview-placeholder">
                Paste an image URL below to preview
              </div>
            </div>
            <div class="form-group mt-1">
              <label>Image URL</label>
              <input
                v-model="imageUrlInput"
                type="text"
                class="regular full bordered"
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            v-if="imageFamily.imageUrl"
            @click="removeFamilyImage"
            class="btn-error-button"
            :disabled="uploadingImage"
          >
            Remove
          </button>
          <div class="spacer"></div>
          <button @click="showImageModal = false" class="btn-clear-button">
            Cancel
          </button>
          <button
            v-if="imageMode === 'upload'"
            @click="uploadFamilyImage"
            :disabled="!selectedImageFile || uploadingImage"
            class="btn-dark-button"
          >
            {{ uploadingImage ? "Uploading..." : "Upload" }}
          </button>
          <button
            v-if="imageMode === 'url'"
            @click="saveImageUrl"
            :disabled="!imageUrlInput || uploadingImage"
            class="btn-dark-button"
          >
            {{ uploadingImage ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════ BULK ASSIGN TAB ═══════════ -->
    <div v-if="activeTab === 'bulk_assign'" class="tab-content">
      <BulkFamilyAssigner />
    </div>

    <!-- ═══════════ BULK UPLOAD MODALS ═══════════ -->
    <UploadComponent
      v-model="showVehicleUpload"
      :allowedTypes="[
        'vehicles_updates',
        'vehicle_catalog_code_assignment',
        'vehicle_families_updates'
      ]"
      @fileUploaded="onVehicleFileUploaded"
    />
    <UploadComponent
      v-model="showFamilyUpload"
      :allowedTypes="['vehicle_families_updates']"
      @fileUploaded="onFamilyFileUploaded"
    />
  </div>
</template>

<script>
import {
  getVehicles,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  getVehicleDeleteInfo,
  getFamilies,
  createFamily,
  updateFamily,
  deleteFamily,
  getFamilyDeleteInfo,
  getFamilyVehicles,
  assignVehicle,
  unassignVehicle,
  saveFamilyImage,
  getVehicleStats,
  bulkAssign
} from "@/api/store/VehicleManagementService.js";
import { getSignedUrl, pushFile } from "@/api/store/s3.js";
import {
  requestReport,
  pollReportUntilDone,
  signAndDownloadFile
} from "@/api/store/ReportGeneratorService.js";
import UploadComponent from "@/components/UploadComponent.vue";
import BulkFamilyAssigner from "@/components/product/applications/BulkFamilyAssigner.vue";

export default {
  name: "ManageVehicles",
  components: {
    UploadComponent,
    BulkFamilyAssigner
  },
  data() {
    return {
      activeTab: "vehicles",
      stats: null,

      // Vehicles
      vehicles: [],
      vehicleTotal: 0,
      vehiclePage: 0,
      vehiclesPerPage: 50,
      loadingVehicles: false,
      vehicleFilters: {
        make: "",
        model: "",
        year: "",
        engine_name: "",
        region: "",
        catalog_code: "",
        active: ""
      },

      // Families
      families: [],
      allFamilies: [],
      loadingFamilies: false,
      familyFilters: {
        catalog_code: "",
        serie: "",
        family: "",
        active: ""
      },

      // Vehicle Modal
      showVehicleModal: false,
      editingVehicle: {},
      savingVehicle: false,

      // Family Modal
      showFamilyModal: false,
      editingFamily: {},
      savingFamily: false,

      // Assign Modal
      showAssignModal: false,
      assignTarget: {},
      assignCatalogCode: "",
      savingAssign: false,

      // Family Vehicles Modal
      showFamilyVehiclesModal: false,
      viewingFamily: {},
      familyVehiclesList: [],
      loadingFamilyVehicles: false,

      // Add Vehicles to Family Modal
      showAddVehiclesModal: false,
      addVehiclesFilters: {
        make: "",
        model: "",
        year: "",
        engine_name: "",
        region: ""
      },
      addVehiclesUnassignedOnly: true,
      addVehiclesResults: [],
      addVehiclesSelected: [],
      loadingAddVehicles: false,
      savingAddVehicles: false,

      // Image Modal
      showImageModal: false,
      imageFamily: {},
      selectedImageFile: null,
      imagePreview: null,
      uploadingImage: false,
      imageMode: "upload",
      imageUrlInput: "",

      // Bulk Upload
      showVehicleUpload: false,
      showFamilyUpload: false,

      // Export
      exportingVehicles: false
    };
  },
  created() {
    this.vehicleDebounceTimer = null;
    this.familyDebounceTimer = null;
  },
  computed: {
    vehicleTotalPages() {
      return Math.ceil(this.vehicleTotal / this.vehiclesPerPage);
    },
    allAddVehiclesSelected() {
      return (
        this.addVehiclesResults.length > 0 &&
        this.addVehiclesResults.every(v =>
          this.addVehiclesSelected.includes(v.id)
        )
      );
    }
  },
  mounted() {
    this.loadStats();
    this.loadVehicles();
    this.loadAllFamilies();
  },
  methods: {
    // ─── DATA LOADING ───

    async loadStats() {
      try {
        this.stats = await getVehicleStats();
      } catch (e) {
        console.error("Error loading stats", e);
      }
    },

    async loadVehicles() {
      this.loadingVehicles = true;
      try {
        const params = {
          page: this.vehiclePage,
          per_page: this.vehiclesPerPage
        };
        Object.entries(this.vehicleFilters).forEach(([k, v]) => {
          if (v !== "") params[k] = v;
        });
        const result = await getVehicles(params);
        this.vehicles = result.vehicles;
        this.vehicleTotal = result.total;
      } catch (e) {
        this.$toast.error("Error loading vehicles");
        console.error(e);
      } finally {
        this.loadingVehicles = false;
      }
    },

    async loadFamilies() {
      this.loadingFamilies = true;
      try {
        const params = {};
        Object.entries(this.familyFilters).forEach(([k, v]) => {
          if (v !== "") params[k] = v;
        });
        this.families = await getFamilies(params);
      } catch (e) {
        this.$toast.error("Error loading families");
        console.error(e);
      } finally {
        this.loadingFamilies = false;
      }
    },

    async loadAllFamilies() {
      try {
        this.allFamilies = await getFamilies({ active: 1 });
      } catch (e) {
        console.error(e);
      }
    },

    debouncedLoadVehicles() {
      clearTimeout(this.vehicleDebounceTimer);
      this.vehicleDebounceTimer = setTimeout(() => {
        this.vehiclePage = 0;
        this.loadVehicles();
      }, 400);
    },

    debouncedLoadFamilies() {
      clearTimeout(this.familyDebounceTimer);
      this.familyDebounceTimer = setTimeout(() => {
        this.loadFamilies();
      }, 400);
    },

    clearVehicleFilters() {
      this.vehicleFilters = {
        make: "",
        model: "",
        year: "",
        engine_name: "",
        region: "",
        catalog_code: "",
        active: ""
      };
      this.vehiclePage = 0;
      this.loadVehicles();
    },

    clearFamilyFilters() {
      this.familyFilters = {
        catalog_code: "",
        serie: "",
        family: "",
        active: ""
      };
      this.loadFamilies();
    },

    // ─── VEHICLE CRUD ───

    openVehicleModal(vehicle) {
      if (vehicle) {
        this.editingVehicle = {
          id: vehicle.id,
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          aspiration: vehicle.aspiration,
          cylinders: vehicle.cylinders,
          block_type: vehicle.blockType,
          engine_liter: vehicle.engineLiter,
          engine_name: vehicle.engineName,
          region: vehicle.region,
          active: vehicle.active,
          catalog_code: vehicle.catalogCode || null
        };
      } else {
        this.editingVehicle = {
          id: null,
          year: new Date().getFullYear(),
          make: "",
          model: "",
          aspiration: "",
          cylinders: null,
          block_type: "",
          engine_liter: null,
          engine_name: "",
          region: "",
          active: 1,
          catalog_code: null
        };
      }
      this.showVehicleModal = true;
    },

    async saveVehicle() {
      if (
        !this.editingVehicle.year ||
        !this.editingVehicle.make ||
        !this.editingVehicle.model
      ) {
        this.$toast.warning("Year, Make, and Model are required");
        return;
      }
      this.savingVehicle = true;
      try {
        if (this.editingVehicle.id) {
          await updateVehicle(this.editingVehicle);
          this.$toast.success("Vehicle updated");
        } else {
          await createVehicle(this.editingVehicle);
          this.$toast.success("Vehicle created");
        }
        this.showVehicleModal = false;
        this.loadVehicles();
        this.loadStats();
      } catch (e) {
        this.$toast.error(e.response?.data?.error || "Error saving vehicle");
      } finally {
        this.savingVehicle = false;
      }
    },

    async toggleVehicleActive(vehicle) {
      const newActive = vehicle.active == 1 ? 0 : 1;
      try {
        await updateVehicle({ id: vehicle.id, active: newActive });
        vehicle.active = newActive;
        this.$toast.success(
          newActive ? "Vehicle activated" : "Vehicle deactivated"
        );
        this.loadStats();
      } catch (e) {
        this.$toast.error("Error updating vehicle status");
      }
    },

    async removeVehicle(vehicle) {
      try {
        const info = await getVehicleDeleteInfo(vehicle.id);
        let msg = `Delete vehicle #${vehicle.id} (${vehicle.year} ${vehicle.make} ${vehicle.model})?`;
        if (info.applicationCount > 0) {
          msg += `\n\nThis will also remove ${info.applicationCount} application relation(s)`;
          if (info.affectedProducts && info.affectedProducts.length > 0) {
            const productList = info.affectedProducts.slice(0, 10).join(", ");
            msg += ` across product(s): ${productList}`;
            if (info.affectedProducts.length > 10) {
              msg += ` and ${info.affectedProducts.length - 10} more`;
            }
          }
          msg += ".";
        }
        if (!confirm(msg)) return;
        await deleteVehicle(vehicle.id);
        this.$toast.success("Vehicle deleted");
        this.loadVehicles();
        this.loadStats();
      } catch (e) {
        this.$toast.error("Error deleting vehicle");
      }
    },

    // ─── FAMILY CRUD ───

    openFamilyModal(family) {
      if (family) {
        this.editingFamily = {
          _isNew: false,
          catalog_code: family.catalogCode,
          serie: family.serie,
          family: family.family,
          active: family.active
        };
      } else {
        this.editingFamily = {
          _isNew: true,
          catalog_code: "",
          serie: "",
          family: "",
          active: 1
        };
      }
      this.showFamilyModal = true;
    },

    async saveFamily() {
      if (
        !this.editingFamily.catalog_code ||
        !this.editingFamily.serie ||
        !this.editingFamily.family
      ) {
        this.$toast.warning("All fields are required");
        return;
      }
      this.savingFamily = true;
      try {
        if (this.editingFamily._isNew) {
          await createFamily(this.editingFamily);
          this.$toast.success("Family created");
        } else {
          await updateFamily(this.editingFamily);
          this.$toast.success("Family updated");
        }
        this.showFamilyModal = false;
        this.loadFamilies();
        this.loadAllFamilies();
        this.loadStats();
      } catch (e) {
        this.$toast.error(e.response?.data?.error || "Error saving family");
      } finally {
        this.savingFamily = false;
      }
    },

    async removeFamily(family) {
      try {
        const info = await getFamilyDeleteInfo(family.catalogCode);
        let msg = `Delete family "${family.family}" (${family.catalogCode})?`;
        const details = [];
        if (info.vehicleCount > 0) {
          details.push(`${info.vehicleCount} vehicle(s) will be unlinked`);
        }
        if (info.applicationCount > 0) {
          details.push(
            `${info.applicationCount} application relation(s) exist across those vehicles`
          );
        }
        if (details.length > 0) {
          msg += `\n\n${details.join(" and ")}.`;
        }
        if (!confirm(msg)) return;
        await deleteFamily(family.catalogCode);
        this.$toast.success("Family deleted");
        this.loadFamilies();
        this.loadAllFamilies();
        this.loadStats();
      } catch (e) {
        this.$toast.error("Error deleting family");
      }
    },

    // ─── ASSIGN / UNASSIGN ───

    openAssignModal(vehicle) {
      this.assignTarget = vehicle;
      this.assignCatalogCode = vehicle.catalogCode || "";
      this.showAssignModal = true;
    },

    async saveAssignment() {
      this.savingAssign = true;
      try {
        if (this.assignCatalogCode) {
          await assignVehicle(this.assignTarget.id, this.assignCatalogCode);
          this.$toast.success("Vehicle assigned to family");
        } else {
          await unassignVehicle(this.assignTarget.id);
          this.$toast.success("Vehicle unassigned from family");
        }
        this.showAssignModal = false;
        this.loadVehicles();
        this.loadFamilies();
        this.loadStats();
      } catch (e) {
        this.$toast.error("Error updating assignment");
      } finally {
        this.savingAssign = false;
      }
    },

    // ─── FAMILY VEHICLES ───

    async viewFamilyVehicles(family) {
      this.viewingFamily = family;
      this.showFamilyVehiclesModal = true;
      this.loadingFamilyVehicles = true;
      try {
        this.familyVehiclesList = await getFamilyVehicles(family.catalogCode);
      } catch (e) {
        this.$toast.error("Error loading family vehicles");
      } finally {
        this.loadingFamilyVehicles = false;
      }
    },

    async unassignFromFamily(vehicle) {
      if (
        !confirm(
          `Remove ${vehicle.year} ${vehicle.make} ${vehicle.model} from this family?`
        )
      )
        return;
      try {
        await unassignVehicle(vehicle.id);
        this.familyVehiclesList = this.familyVehiclesList.filter(
          v => v.id !== vehicle.id
        );
        this.$toast.success("Vehicle removed from family");
        this.loadStats();
      } catch (e) {
        this.$toast.error("Error removing vehicle");
      }
    },

    // ─── IMAGE UPLOAD ───

    openImageUpload(family) {
      this.imageFamily = family;
      this.selectedImageFile = null;
      this.imagePreview = null;
      this.imageMode = "upload";
      this.imageUrlInput = family.imageUrl || "";
      this.showImageModal = true;
    },

    async saveImageUrl() {
      if (!this.imageUrlInput) return;
      this.uploadingImage = true;
      try {
        await saveFamilyImage(this.imageFamily.catalogCode, this.imageUrlInput);
        this.$toast.success("Image URL saved");
        this.showImageModal = false;
        this.loadFamilies();
      } catch (e) {
        this.$toast.error("Error saving image URL");
        console.error(e);
      } finally {
        this.uploadingImage = false;
      }
    },

    onImageSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.selectedImageFile = file;
      const reader = new FileReader();
      reader.onload = e => {
        this.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    async uploadFamilyImage() {
      if (!this.selectedImageFile) return;
      this.uploadingImage = true;
      try {
        const ext = "." + this.selectedImageFile.name.split(".").pop();
        const signedUrl = await getSignedUrl(this.selectedImageFile.type, ext);
        await pushFile(
          signedUrl,
          this.selectedImageFile,
          this.selectedImageFile.type
        );
        // Extract the final URL (remove query params from signed URL)
        const imageUrl = signedUrl.split("?")[0];
        await saveFamilyImage(this.imageFamily.catalogCode, imageUrl);
        this.$toast.success("Image uploaded");
        this.showImageModal = false;
        this.loadFamilies();
      } catch (e) {
        this.$toast.error("Error uploading image");
        console.error(e);
      } finally {
        this.uploadingImage = false;
      }
    },

    onVehicleFileUploaded() {
      this.$toast.success("Vehicle bulk upload completed");
      this.loadVehicles();
      this.loadStats();
    },

    onFamilyFileUploaded() {
      this.$toast.success("Family bulk upload completed");
      this.loadFamilies();
      this.loadAllFamilies();
      this.loadStats();
    },

    async exportVehiclesWithFamilies() {
      this.exportingVehicles = true;
      try {
        const report = await requestReport({
          fileType: "vehicles_family_export",
          otherOptions: {}
        });
        const result = await pollReportUntilDone(report.reportId);
        if (result.status === "created" && result.s3Path) {
          await signAndDownloadFile(result.s3Path, result.s3Route);
          this.$toast.success("Vehicles exported successfully");
        } else {
          this.$toast.error("Export failed");
        }
      } catch (e) {
        this.$toast.error("Error exporting vehicles");
        console.error(e);
      } finally {
        this.exportingVehicles = false;
      }
    },

    // ─── ADD VEHICLES TO FAMILY ───

    openAddVehiclesModal() {
      this.addVehiclesFilters = {
        make: "",
        model: "",
        year: "",
        engine_name: "",
        region: ""
      };
      this.addVehiclesUnassignedOnly = true;
      this.addVehiclesResults = [];
      this.addVehiclesSelected = [];
      this.showAddVehiclesModal = true;
    },

    debouncedSearchAddVehicles() {
      clearTimeout(this.addVehiclesDebounceTimer);
      this.addVehiclesDebounceTimer = setTimeout(() => {
        this.searchAddVehicles();
      }, 400);
    },

    async searchAddVehicles() {
      const filters = this.addVehiclesFilters;
      const hasFilter = Object.values(filters).some(v => v !== "");
      if (!hasFilter) {
        this.addVehiclesResults = [];
        return;
      }
      this.loadingAddVehicles = true;
      try {
        const params = { page: 0, per_page: 200, active: 1 };
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== "") params[k] = v;
        });
        const result = await getVehicles(params);
        let list = result.vehicles;
        if (this.addVehiclesUnassignedOnly) {
          list = list.filter(v => !v.catalogCode);
        } else {
          list = list.filter(
            v => v.catalogCode !== this.viewingFamily.catalogCode
          );
        }
        this.addVehiclesResults = list;
      } catch (e) {
        this.$toast.error("Error searching vehicles");
        console.error(e);
      } finally {
        this.loadingAddVehicles = false;
      }
    },

    toggleAddVehicleSelection(id) {
      const idx = this.addVehiclesSelected.indexOf(id);
      if (idx === -1) {
        this.addVehiclesSelected.push(id);
      } else {
        this.addVehiclesSelected.splice(idx, 1);
      }
    },

    toggleSelectAllAddVehicles() {
      if (this.allAddVehiclesSelected) {
        const ids = this.addVehiclesResults.map(v => v.id);
        this.addVehiclesSelected = this.addVehiclesSelected.filter(
          id => !ids.includes(id)
        );
      } else {
        const ids = this.addVehiclesResults.map(v => v.id);
        ids.forEach(id => {
          if (!this.addVehiclesSelected.includes(id)) {
            this.addVehiclesSelected.push(id);
          }
        });
      }
    },

    async confirmAddVehicles() {
      if (this.addVehiclesSelected.length === 0) return;
      const reassigning = this.addVehiclesResults.filter(
        v =>
          this.addVehiclesSelected.includes(v.id) &&
          v.catalogCode &&
          v.catalogCode !== this.viewingFamily.catalogCode
      );
      if (reassigning.length > 0) {
        if (
          !confirm(
            `${reassigning.length} of the selected vehicle(s) are already assigned to a different family. They will be reassigned to "${this.viewingFamily.family}". Continue?`
          )
        ) {
          return;
        }
      }
      this.savingAddVehicles = true;
      try {
        await bulkAssign(
          this.addVehiclesSelected,
          this.viewingFamily.catalogCode
        );
        this.$toast.success(
          `${this.addVehiclesSelected.length} vehicle(s) added to family`
        );
        this.showAddVehiclesModal = false;
        this.familyVehiclesList = await getFamilyVehicles(
          this.viewingFamily.catalogCode
        );
        this.loadVehicles();
        this.loadFamilies();
        this.loadStats();
      } catch (e) {
        this.$toast.error("Error adding vehicles to family");
        console.error(e);
      } finally {
        this.savingAddVehicles = false;
      }
    },

    async removeFamilyImage() {
      if (!confirm("Remove the image for this family?")) return;
      this.uploadingImage = true;
      try {
        await saveFamilyImage(this.imageFamily.catalogCode, null);
        this.$toast.success("Image removed");
        this.showImageModal = false;
        this.loadFamilies();
      } catch (e) {
        this.$toast.error("Error removing image");
      } finally {
        this.uploadingImage = false;
      }
    }
  },
  watch: {
    activeTab(tab) {
      if (tab === "families" && this.families.length === 0) {
        this.loadFamilies();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/css/App.scss";

#manage-vehicles {
  padding: 1.5rem;
}

.page-header {
  margin-bottom: 1.5rem;

  .title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }
}

.stats-bar {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;

  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: #f9fafb;
    padding: 0.75rem 1.25rem;
    border-radius: 0.5rem;
    border: 1px solid #e5e7eb;
    min-width: 100px;

    .stat-value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #111827;
    }

    .stat-label {
      font-size: 0.75rem;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    &.warn .stat-value {
      color: #d97706;
    }
  }
}

.navigation .bar {
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 1.5rem;

  .tablinks {
    padding: 0.75rem 1.5rem;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    font-weight: 500;
    color: white;
    margin-bottom: -2px;
    transition: all 0.2s;

    &:hover {
      color: #374151;
      background: #f9fafb;
    }

    &.active {
      color: #111827;
      border-bottom-color: #111827;
    }
  }
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;

  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 1rem;

    .per-page {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      label {
        font-weight: 500;
      }
      select {
        width: 70px;
      }
    }
  }
}

.filter-bar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  input {
    max-width: 160px;
  }
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  thead {
    background: #111827;
    color: white;

    th {
      padding: 0.625rem 0.75rem;
      text-align: left;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      transition: background 0.15s;

      &:nth-child(even) {
        background: #f9fafb;
      }
      &:hover {
        background: #fef3c7;
      }

      td {
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid #f3f4f6;
        white-space: nowrap;
      }
    }
  }

  &.compact {
    font-size: 0.8125rem;
    td,
    th {
      padding: 0.375rem 0.5rem;
    }
  }

  .id-cell {
    color: #6b7280;
    font-size: 0.75rem;
  }

  .empty-row {
    text-align: center;
    padding: 2rem !important;
    color: #6b7280;
  }
}

.code-badge {
  background: #f3f4f6;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  font-family: monospace;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;

  &.active {
    background: #10b981;
  }
  &.inactive {
    background: #ef4444;
  }
}

.text-muted {
  color: #9ca3af;
}

.status-toggle {
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;

  &.is-active {
    background: #d1fae5;
    color: #065f46;

    &:hover {
      background: #fecaca;
      color: #991b1b;
    }
  }

  &.is-inactive {
    background: #fee2e2;
    color: #991b1b;

    &:hover {
      background: #d1fae5;
      color: #065f46;
    }
  }
}

.actions-cell {
  display: flex;
  gap: 0.25rem;

  .action-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1rem;
    padding: 0.25rem;
    transition: transform 0.15s;

    &:hover {
      transform: scale(1.2);
    }
    &.delete:hover {
      filter: brightness(0.8);
    }
  }
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  font-size: 0.875rem;

  .pagination-btns {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    button:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}

// ─── Families Grid ───

.families-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

.family-card {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  background: white;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.inactive {
    opacity: 0.6;
  }

  .family-image-container {
    position: relative;
    height: 160px;
    background: #f3f4f6;
    cursor: pointer;
    overflow: hidden;

    .family-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .family-image-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: #9ca3af;

      span {
        font-size: 2rem;
      }
      small {
        font-size: 0.75rem;
        margin-top: 0.25rem;
      }
    }

    .image-overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
      opacity: 0;
      transition: opacity 0.2s;
    }

    &:hover .image-overlay {
      opacity: 1;
    }
  }

  .family-info {
    padding: 1rem;

    .family-name {
      font-size: 1.125rem;
      font-weight: 600;
      margin: 0.5rem 0 0.25rem;
    }

    .family-serie {
      font-size: 0.8125rem;
      color: #6b7280;
      margin: 0 0 0.5rem;
    }

    .family-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .vehicle-count {
        font-size: 0.8125rem;
        color: #6b7280;
      }
    }
  }

  .family-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.25rem;
    padding: 0.5rem 1rem;
    border-top: 1px solid #f3f4f6;

    .action-btn {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 1.125rem;
      padding: 0.25rem 0.5rem;
      transition: transform 0.15s;

      &:hover {
        transform: scale(1.15);
      }
    }
  }
}

// ─── Modals ───

.modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

  &.modal-small {
    max-width: 500px;
  }
  &.modal-medium {
    max-width: 700px;
  }
  &.modal-large {
    max-width: 900px;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .modal-close {
    background: none;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    color: #6b7280;
    padding: 0.25rem 0.5rem;

    &:hover {
      color: #111827;
    }
  }
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;

  .spacer {
    flex: 1;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.form-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  label {
    display: block;
    font-weight: 500;
    margin-bottom: 0.375rem;
    font-size: 0.875rem;
    color: #374151;
  }
}

.assign-info {
  margin-bottom: 1rem;
  font-size: 0.9375rem;
}

.image-preview-box {
  width: 100%;
  height: 200px;
  background: #f3f4f6;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;

  .preview-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .preview-placeholder {
    color: #9ca3af;
  }
}

.loading-state {
  text-align: center;
  padding: 3rem;
  color: #6b7280;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6b7280;
  grid-column: 1 / -1;
}

.image-mode-toggle {
  display: flex;
  gap: 0;
  margin-bottom: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;

  .toggle-btn {
    flex: 1;
    padding: 0.625rem 1rem;
    border: none;
    background: #f9fafb;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.875rem;
    color: #6b7280;
    transition: all 0.2s;

    &:not(:last-child) {
      border-right: 1px solid #e5e7eb;
    }

    &.active {
      background: #111827;
      color: white;
    }

    &:hover:not(.active) {
      background: #e5e7eb;
    }
  }
}

.mt-1 {
  margin-top: 1rem;
}
.mb-0 {
  margin-bottom: 0;
}
.mb-1 {
  margin-bottom: 1rem;
}

.family-vehicles-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.inline-check {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
}

.selectable-row {
  cursor: pointer;
}
</style>
